import { get, includes } from "lodash";
import axiosLib from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { Auth } from "@/services/auth";
import qs from "query-string";
import { restoreSession } from "@/services/restoreSession";

export const axios = axiosLib.create({
  paramsSerializer: params => qs.stringify(params),
  xsrfCookieName: "csrf_token",
  xsrfHeaderName: "X-CSRF-TOKEN",
});

axios.interceptors.response.use(response => response.data);

/**
 * No auth-refresh / CSRF-refresh interceptors. Used only for loading protected public dashboards:
 * a 401 must surface to {@link PublicDashboardPage} instead of triggering session restore / retry.
 * Does not attach Authorization: Key — the share token is passed as ?api_key= on these routes.
 */
export const axiosPublicDashboard = axiosLib.create({
  paramsSerializer: params => qs.stringify(params),
  xsrfCookieName: "csrf_token",
  xsrfHeaderName: "X-CSRF-TOKEN",
});
axiosPublicDashboard.interceptors.response.use(response => response.data);
axiosPublicDashboard.interceptors.request.use(config => {
  if (typeof window !== "undefined") {
    const searchParams = qs.parse(window.location.search || "");
    const orgSlug = searchParams.org_slug;
    const isApiRequest =
      typeof config.url === "string" && (config.url.startsWith("api/") || config.url.startsWith("/api/"));
    if (orgSlug && isApiRequest && (!config.params || !config.params.org_slug)) {
      config.params = {
        ...(config.params || {}),
        org_slug: orgSlug,
      };
    }
  }
  return config;
});

export const csrfRefreshInterceptor = createAuthRefreshInterceptor(
  axios,
  error => {
    const message = get(error, "response.data.message");
    if (error.isAxiosError && includes(message, "CSRF")) {
      return axios.get("/ping");
    } else {
      return Promise.reject(error);
    }
  },
  { statusCodes: [400] }
);

export const sessionRefreshInterceptor = createAuthRefreshInterceptor(
  axios,
  error => {
    const status = parseInt(get(error, "response.status"));
    const message = get(error, "response.data.message");
    // TODO: In axios@0.9.1 this check could be replaced with { skipAuthRefresh: true } flag. See axios-auth-refresh docs
    const requestUrl = get(error, "config.url") || "";
    const isPublicDashboardAuthRequest =
      requestUrl.includes("api/dashboards/public/") ||
      requestUrl.includes("/api/dashboards/public/");
    if (
      error.isAxiosError &&
      !isPublicDashboardAuthRequest &&
      (status === 401 || includes(message, "Please login")) &&
      requestUrl !== "api/session"
    ) {
      return restoreSession();
    }
    return Promise.reject(error);
  },
  {
    statusCodes: [401, 404],
    pauseInstanceWhileRefreshing: false, // According to docs, `false` is default value, but in fact it's not :-)
  }
);

axios.interceptors.request.use(config => {
  const apiKey = Auth.getApiKey();
  // Public dashboard fetch uses ?api_key=... only; skipping Key here allows Basic auth or
  // custom headers for public-link login without conflicting with get_api_key_from_request().
  if (apiKey && !config.skipAuthorizationHeader) {
    config.headers.Authorization = `Key ${apiKey}`;
  }

  // In multi-org public dashboard mode, org_slug is often provided as query param on
  // the shared URL. Forward it on every API request so anonymous/public sessions don't
  // fail with 404 after successful dashboard auth.
  if (typeof window !== "undefined") {
    const searchParams = qs.parse(window.location.search || "");
    const orgSlug = searchParams.org_slug;
    const isApiRequest = typeof config.url === "string" && (config.url.startsWith("api/") || config.url.startsWith("/api/"));
    if (orgSlug && isApiRequest && (!config.params || !config.params.org_slug)) {
      config.params = {
        ...(config.params || {}),
        org_slug: orgSlug,
      };
    }
  }

  return config;
});
